import { Injectable, NgZone } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { OAuthService } from 'angular-oauth2-oidc';
import { Capacitor } from '@capacitor/core';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AuthService } from '../../auth.service';
import { ervenyesJogkorNezet } from '../../jogkor-nezet-tarolo';

/**
 *  SZERKESZTÉSI ZÁR a felületen (backend/szerkesztes-zar.js).
 *
 *  Egy rekord szerkesztőjének megnyitásakor:
 *      this.zar = await this.szerkZar.foglal('naplo', 'kettos_tetel', t.id, { ujratolt: () => this.betolt(t.id) });
 *  bezárásakor (Mégse, modal eltűnése, másik rekord, ngOnDestroy):
 *      this.zar?.felenged(); this.zar = null;
 *  a mentés gombja: [disabled]="… || (zar && !zar.irhato)", fölötte: <app-szerk-zar-sav [zar]="zar">.
 *
 *  A szolgáltatás:
 *   – félpercenként megújítja a zárakat (a kiszolgálón 2 perc a bérlet);
 *   – ha más tartja a rekordot, félpercenként újra próbálja, és amint felszabadul, az `ujratolt`-tal
 *     friss adatot kér, és szerkeszthetővé teszi;
 *   – 30 perc tétlenség után nem újít (a zár magától felszabadul), mozdulásra visszakéri – ha
 *     közben senki nem nyúlt hozzá, ugyanazzal a tokennel folytatódik;
 *   – a lap bezárásakor és kilépéskor (keepalive kéréssel) azonnal elengedi;
 *   – a módosító kérések fejlécébe (X-Szerk-Zar) beírja a lap zárjait (SzerkZarInterceptor), így a
 *     kiszolgáló a mentést is ellenőrzi.
 */

export type ZarModul = 'naplo' | 'ber' | 'szamlazo' | 'inventario' | 'eszkoz' | 'gepjarmu';
export type ZarAllapot = 'kerem' | 'foglalt' | 'masnal' | 'elveszett' | 'szunetel' | 'hiba' | 'zarNelkul' | 'felengedve';

export interface ZarFoglalo { nev: string; sajat: boolean; kezdve?: string; frissitve?: string; }

const MEGUJIT_MS = 30 * 1000;
const TETLEN_MS = 30 * 60 * 1000;

export class SzerkZar {
  allapot: ZarAllapot = 'kerem';
  token: string | null = null;
  foglalo: ZarFoglalo | null = null;
  /** Az utolsó hiba / figyelmeztetés szövege (a sávnak). */
  uzenet = '';
  /** @internal hány szerkesztő tartja ugyanebben a lapban */
  hivatkozas = 1;

  constructor(
    readonly modul: ZarModul,
    readonly tipus: string,
    readonly rekord: string,
    private readonly svc: SzerkZarService,
    public ujratolt?: () => unknown,
  ) {}

  /** Menthető-e a rekord ebben a lapban. Zár nélküli / átmenetileg hibás állapotban igen (a kiszolgáló véd). */
  get irhato(): boolean {
    return this.allapot === 'foglalt' || this.allapot === 'zarNelkul' || this.allapot === 'hiba' || this.allapot === 'szunetel';
  }
  get kulcs(): string { return `${this.tipus}|${this.rekord}`; }
  /** A szerkesztő bezárta (felengedte) – egy késve érkező válasz már nem írhatja át. */
  get lezart(): boolean { return this.allapot === 'felengedve'; }

  /** A saját (másik ablakban nyitott) vagy – adminnak – más zárjának átvétele. */
  atvesz(): Promise<void> { return this.svc.kerZar(this, true); }
  /** Azonnali újrapróbálás (különben félpercenként magától). */
  ujraProbal(): Promise<void> { return this.svc.kerZar(this, false); }
  /** A szerkesztő bezárásakor. Többszöri hívás ártalmatlan. */
  felenged(): void { this.svc.felenged(this); }
}

@Injectable({ providedIn: 'root' })
export class SzerkZarService {
  private readonly aktiv = new Map<string, SzerkZar>();
  private idozito: any = null;
  private utolsoMozdulas = Date.now();

  constructor(private http: HttpClient, private oauth: OAuthService, private auth: AuthService, private zone: NgZone) {
    if (typeof window === 'undefined') return;
    const mozdul = () => {
      const most = Date.now();
      if (most - this.utolsoMozdulas < 2000) return;
      this.utolsoMozdulas = most;
      //  Tétlenség után: a szünetelő zárakat azonnal visszakérjük.
      for (const z of this.aktiv.values()) if (z.allapot === 'szunetel') this.zone.run(() => this.kerZar(z, false));
    };
    //  FONTOS: ezeket a nagy gyakoriságú aktivitás-figyelőket a ZÓNÁN KÍVÜL regisztráljuk.
    //  In-zone regisztrálva minden pointerdown/keydown/… Angular változásdetektálást váltott ki,
    //  és a trackBy nélküli *ngFor-listák (pl. a napló/kontír gombsorai) ilyenkor ÚJRAÉPÜLTEK –
    //  ha ez egy egérlenyomás alá esett, a mousedown/mouseup más elemre került, és a böngésző NEM
    //  generált `click`-et → a gombok „nem csináltak semmit" (szaggatottan, sok helyen). A `mozdul`
    //  maga zone.run-nal tér vissza a zónába, amikor tényleg kell (szünetelő zár visszakérése).
    this.zone.runOutsideAngular(() => {
      for (const e of ['keydown', 'pointerdown', 'input', 'wheel', 'touchstart']) {
        document.addEventListener(e, mozdul, { capture: true, passive: true });
      }
    });
    //  A lap bezárása / elhagyása és a kilépés: azonnali elengedés (keepalive – a lap eltűnése után is kimegy).
    window.addEventListener('pagehide', () => this.mindetElengedKeepalive());
    window.addEventListener('cronom-kilepes', () => this.mindetElengedKeepalive());
    //  Alvásból / háttérből visszatérve: a megújítás azonnal (a háttérben az időzítő ritkábban fut).
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') this.zone.run(() => this.kor());
    });
  }

  /**
   *  Zár kérése a rekordra. Új (még mentetlen) rekordnál (`rekord` üres) nincs zár – null.
   *  Mindig visszatér (hibánál is): az állapotot a fogantyú mondja meg.
   */
  async foglal(modul: ZarModul, tipus: string, rekord: string | null | undefined, opciok: { ujratolt?: () => unknown } = {}): Promise<SzerkZar | null> {
    const r = String(rekord ?? '').trim();
    if (!r) return null;
    const kulcs = `${tipus}|${r}`;
    const meglevo = this.aktiv.get(kulcs);
    if (meglevo && meglevo.allapot !== 'felengedve') {
      meglevo.hivatkozas += 1;
      if (opciok.ujratolt) meglevo.ujratolt = opciok.ujratolt;
      return meglevo;
    }
    const z = new SzerkZar(modul, tipus, r, this, opciok.ujratolt);
    this.aktiv.set(kulcs, z);
    this.idozitoIndit();
    await this.kerZar(z, false);
    return z;
  }

  /** @internal a zár (újra)kérése; az állapotot a fogantyúba írja. */
  async kerZar(z: SzerkZar, atvesz: boolean): Promise<void> {
    if (z.allapot === 'felengedve') return;
    const elozo = z.allapot;
    try {
      const v: any = await firstValueFrom(this.http.post(
        `/api/szerk-zar/${z.modul}/foglal/${encodeURIComponent(z.tipus)}/${encodeURIComponent(z.rekord)}`,
        { token: z.token, atvesz }, { headers: { 'X-Silent': '1' } }));
      if (z.lezart) {          // közben bezárták – a friss zárat is elengedjük
        if (v?.token) { z.token = v.token; this.elenged([z]); }
        return;
      }
      z.foglalo = null;
      z.uzenet = '';
      if (v?.zarNelkul || !v?.token) { z.allapot = 'zarNelkul'; z.token = null; return; }
      z.token = v.token;
      //  Más tartotta (nálunk csak olvasható volt, tehát nincs mentetlen módosítás), most felszabadult:
      //  friss adatot töltünk, és szerkeszthető. Újratöltő nélkül a sáv kéri az újratöltést.
      if (elozo === 'masnal') {
        if (z.ujratolt) {
          z.allapot = 'foglalt';
          try { await z.ujratolt(); } catch { /* a betöltő maga jelzi a hibát */ }
        } else {
          z.allapot = 'elveszett';
        }
        return;
      }
      //  Nálunk szerkesztés alatt volt (tétlenség, alvás), és közben más is megnyitotta: lehet mentetlen
      //  módosítás a képernyőn, ezért NEM töltünk újra magunktól – a sáv kéri. A kapott (friss) zár a miénk,
      //  az újratöltés után ezzel folytatjuk.
      if (v.elveszett) { z.allapot = 'elveszett'; return; }
      z.allapot = 'foglalt';
    } catch (e: any) {
      if (z.lezart) return;
      const he = e as HttpErrorResponse;
      if (he?.status === 423) {
        z.allapot = 'masnal';
        z.foglalo = he.error?.foglalo || { nev: 'egy másik felhasználó', sajat: false };
        z.uzenet = atvesz && !z.foglalo?.sajat ? 'Más felhasználó zárját csak a modul adminja veheti át.' : '';
        return;
      }
      if (he?.status === 401 || he?.status === 403) {
        z.allapot = 'hiba';
        z.uzenet = he.error?.error || 'A szerkesztési zárhoz nincs jogosultság.';
        return;
      }
      z.allapot = 'hiba';
      z.uzenet = he?.error?.error || 'A szerkesztési zár most nem érhető el – próbálkozom.';
    }
  }

  /** @internal */
  felenged(z: SzerkZar): void {
    if (z.allapot === 'felengedve') return;
    z.hivatkozas -= 1;
    if (z.hivatkozas > 0) return;
    const voltToken = z.token;
    z.allapot = 'felengedve';
    if (this.aktiv.get(z.kulcs) === z) this.aktiv.delete(z.kulcs);
    if (voltToken) this.elenged([z]);
    if (!this.aktiv.size) this.idozitoAllj();
  }

  /** Az X-Szerk-Zar fejléc értéke (SzerkZarInterceptor): a lap minden tokenes zárja. */
  fejlec(): string {
    const elemek: string[] = [];
    for (const z of this.aktiv.values()) {
      if (z.token && z.allapot !== 'felengedve') elemek.push(`${encodeURIComponent(z.tipus)}|${encodeURIComponent(z.rekord)}|${z.token}`);
    }
    return elemek.join(',');
  }

  /* ── belső ─────────────────────────────────────────────────────────────── */

  private idozitoIndit() {
    if (this.idozito) return;
    this.idozito = setInterval(() => this.kor(), MEGUJIT_MS);
  }
  private idozitoAllj() {
    if (this.idozito) { clearInterval(this.idozito); this.idozito = null; }
  }

  /** Félperces kör: megújítás, a foglaltak újrapróbálása, tétlenségnél szünet. */
  private async kor() {
    if (!this.aktiv.size) return;
    const tetlen = Date.now() - this.utolsoMozdulas > TETLEN_MS;
    const megujitando = new Map<ZarModul, SzerkZar[]>();
    for (const z of this.aktiv.values()) {
      if (z.allapot === 'foglalt') {
        if (tetlen) { z.allapot = 'szunetel'; continue; }
        if (!megujitando.has(z.modul)) megujitando.set(z.modul, []);
        megujitando.get(z.modul)!.push(z);
      } else if (z.allapot === 'masnal' || z.allapot === 'hiba' || z.allapot === 'kerem') {
        if (!tetlen) this.kerZar(z, false);
      }
    }
    for (const [modul, zarak] of megujitando) {
      try {
        const v: any = await firstValueFrom(this.http.post(`/api/szerk-zar/${modul}/megujit`,
          { zarak: zarak.map((z) => ({ tipus: z.tipus, rekord: z.rekord, token: z.token })) }, { headers: { 'X-Silent': '1' } }));
        const allapotok = new Map<string, string>((v?.zarak || []).map((x: any) => [`${x.tipus}|${x.rekord}`, x.allapot]));
        for (const z of zarak) {
          if (z.allapot !== 'foglalt') continue;
          //  A régi token már nem a miénk: eldobjuk, hogy az újrakérés tiszta lappal (újonnan) foglaljon.
          if (allapotok.get(z.kulcs) === 'elveszett') { z.allapot = 'elveszett'; z.uzenet = ''; z.token = null; }
        }
      } catch {
        //  Hálózati / kiszolgálói hiba: a következő körben újra. A bérlet 2 perc, egy kihagyott kör belefér.
      }
    }
  }

  /** Elengedés a szokásos úton (a lap él). */
  private elenged(zarak: SzerkZar[]) {
    const modulok = new Map<ZarModul, any[]>();
    for (const z of zarak) {
      if (!z.token) continue;
      if (!modulok.has(z.modul)) modulok.set(z.modul, []);
      modulok.get(z.modul)!.push({ tipus: z.tipus, rekord: z.rekord, token: z.token });
    }
    for (const [modul, lista] of modulok) {
      this.http.post(`/api/szerk-zar/${modul}/felenged`, { zarak: lista }, { headers: { 'X-Silent': '1' } })
        .subscribe({ error: () => { /* a bérlet úgyis lejár */ } });
    }
  }

  /**
   *  Elengedés a lap bezárásakor / kilépéskor: keepalive fetch, mert a HttpClient kérését a böngésző a
   *  lappal együtt eldobná. A fejléceket (token, nézetváltás, jogkör-nézet) itt kézzel rakjuk össze –
   *  a fetch nem megy át az interceptorokon.
   */
  private mindetElengedKeepalive() {
    if (!this.aktiv.size) return;
    const modulok = new Map<ZarModul, any[]>();
    for (const z of this.aktiv.values()) {
      if (!z.token || z.allapot === 'felengedve') continue;
      if (!modulok.has(z.modul)) modulok.set(z.modul, []);
      modulok.get(z.modul)!.push({ tipus: z.tipus, rekord: z.rekord, token: z.token });
    }
    if (!modulok.size) return;
    const fej: Record<string, string> = { 'Content-Type': 'application/json' };
    try {
      const token = this.auth.isStandaloneLogin() ? this.auth.getLocalToken() : this.oauth.getAccessToken();
      if (token) fej['Authorization'] = 'Bearer ' + token;
    } catch { /* token nélkül a kiszolgáló elutasítja – a bérlet úgyis lejár */ }
    try {
      const imp = JSON.parse(localStorage.getItem('cronom_impersonalas') || 'null');
      if (imp?.menet) fej['X-Impersonalas-Menet'] = String(imp.menet);
    } catch { /* */ }
    try {
      const n = ervenyesJogkorNezet();
      if (n?.menet) fej['X-Jogkor-Nezet'] = String(n.menet);
    } catch { /* */ }
    const alap = Capacitor.isNativePlatform() ? String(environment.apiOrigin || '').replace(/\/api\/?$/, '') : '';
    for (const [modul, zarak] of modulok) {
      try {
        fetch(`${alap}/api/szerk-zar/${modul}/felenged`, { method: 'POST', keepalive: true, headers: fej, body: JSON.stringify({ zarak }) })
          .catch(() => { /* */ });
      } catch { /* */ }
    }
  }
}
